import { FEEDBACK_RECORDS_EQL_FIELD } from '@modules/common/components/EqlFieldsAutoComplete/constants';
import { EqlFieldName } from '@modules/common/constants';
import { getDurationFromAssistantResponseEqlQuery } from '@modules/oracle/utils';
import {
  CompareMetadata,
  EqlFunctionDeltaOrder,
  EqlFunctionOrder,
  EqlQueryProperties,
  EqlQueryType,
  QuantifyMetadata,
  QuantifyVisualisationType,
  TimeUnit,
} from 'generated/graphql';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import {
  getReversedSourceDetailMap,
  parseExpression,
  toSourceFilter,
} from 'v2/core/repository/eql/parser';
import {
  EQLFunctionName,
  EQLFunctionNameMap,
} from 'v2/core/repository/eql/types';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  TOP_QUANTIFY_SELECTION_OPTIONS,
} from '../constants';
import { quantifyInitialState } from '../store/quantifyInitialState';
import { QuantifyState, SortBy } from '../store/types';

interface getQuantifyStateFromEQLPropertiesParams {
  properties: EqlQueryProperties;
  sourceDetailsMap: ReturnType<typeof useGetSourceDetailsMap>['data'];
  quantifyMetadata: QuantifyMetadata | undefined;
  createdAt: number;
  legacyQuantifyMetadata: string;
}

export const getQuantifyStateFromEQLProperties = ({
  properties,
  sourceDetailsMap,
  quantifyMetadata,
  createdAt,
  legacyQuantifyMetadata,
}: getQuantifyStateFromEQLPropertiesParams): Partial<QuantifyState> => {
  const parsedMetadata = (() => {
    try {
      return legacyQuantifyMetadata
        ? JSON.parse(legacyQuantifyMetadata)
        : undefined;
    } catch {
      return undefined;
    }
  })();

  const getVisualisationType = () => {
    if (quantifyMetadata?.visualisationType) {
      return quantifyMetadata.visualisationType;
    }

    if (parsedMetadata?.metadata?.activeVisualisationType) {
      return parsedMetadata.metadata.activeVisualisationType;
    }

    return QuantifyVisualisationType.Bar;
  };

  const getCompareMetadata = (): CompareMetadata | undefined => {
    if (quantifyMetadata?.compareMetadata) {
      return quantifyMetadata.compareMetadata;
    }

    if (!properties.compareQuery) {
      return undefined;
    }

    const isCompareByWhereClause = !!properties.compareQuery.whereClause;

    if (parsedMetadata?.metadata?.compareQBField && isCompareByWhereClause) {
      return {
        baseQueryName:
          parsedMetadata.metadata.compareQBField.compareQBFieldAName,
        deltaQueryName:
          parsedMetadata.metadata.compareQBField.compareQBFieldBName,
      };
    }

    if (parsedMetadata?.metadata?.compareDurationField) {
      return {
        baseQueryName:
          parsedMetadata.metadata.compareDurationField.compareDurationAName,
        deltaQueryName:
          parsedMetadata.metadata.compareDurationField.compareDurationBName,
      };
    }

    return undefined;
  };

  const whereClause =
    sourceDetailsMap && properties.whereClause
      ? parseExpression(properties.whereClause, sourceDetailsMap)
      : undefined;

  const getAnomalyInfo = () => {
    const anomaly = properties.functions?.find(
      (func) => Object.keys(func?.anomaly ?? {}).length > 0,
    );

    if (!anomaly) {
      return null;
    }
    return {
      threshold: anomaly?.anomaly?.threshold,
      anomalyType: anomaly?.anomaly?.shouldIncludeNonAnomalies,
    };
  };

  const getOrderField = (sortFuncValue: EqlFunctionOrder | undefined) => {
    if (
      sortFuncValue?.value ||
      sortFuncValue?.anomalousValue ||
      sortFuncValue?.volume
    ) {
      return 'value';
    }

    if (!!sortFuncValue?.timestamp) {
      return sortFuncValue?.timestamp.toString();
    }

    if (sortFuncValue?.metadataValue) {
      return 'metadata-value';
    }

    return 'value';
  };

  const getDeltaOrderField = (
    sortFuncValue: EqlFunctionDeltaOrder | undefined,
  ) => {
    if (sortFuncValue?.baseline) {
      return 'prevValue';
    }

    if (sortFuncValue?.inspect) {
      return 'currValue';
    }

    if (sortFuncValue?.absoluteDifference) {
      return 'absValueDelta';
    }

    if (sortFuncValue?.difference) {
      return 'absoluteDelta';
    }

    return 'absoluteDelta';
  };

  const getSortBy = (): SortBy => {
    const sortFunc = properties.functions?.find(
      (func) => Object.keys(func?.order ?? {}).length > 0,
    );

    const deltaSortFunc = properties.functions?.find(
      (func) => Object.keys(func?.deltaOrder ?? {}).length > 0,
    );

    const getField = () => {
      if (sortFunc?.order) {
        return getOrderField(sortFunc?.order);
      }
      if (deltaSortFunc?.deltaOrder) {
        return getDeltaOrderField(deltaSortFunc?.deltaOrder);
      }

      return parsedMetadata?.metadata?.field ?? 'value';
    };

    const getSortOrder = () => {
      if (sortFunc?.order) {
        return !sortFunc?.order?.isDescending;
      }
      if (deltaSortFunc?.deltaOrder) {
        return !deltaSortFunc?.deltaOrder?.isDescending;
      }

      return parsedMetadata?.metadata?.sortBy?.isAsc ?? false;
    };

    return {
      field: getField(),
      isAsc: getSortOrder(),
    };
  };

  //TODO: Fix for quantify that has table eql saved. Not true for all quantify. Remove this after some time.
  const getFixedTopX = (topDataPointsSelectionCount?: number) => {
    if (!topDataPointsSelectionCount) {
      return TOP_QUANTIFY_SELECTION_OPTIONS[0];
    }

    if (TOP_QUANTIFY_SELECTION_OPTIONS.includes(topDataPointsSelectionCount)) {
      return topDataPointsSelectionCount;
    }

    if (parsedMetadata?.metadata?.topDataPointsSelectionCount) {
      return parsedMetadata.metadata.topDataPointsSelectionCount;
    }

    return TOP_QUANTIFY_SELECTION_OPTIONS[0];
  };

  const getTopXProperties = () => {
    const selectionFunc = properties.functions?.find(
      (func) => Object.keys(func?.selection ?? {}).length > 0,
    );

    const paginateFunc = properties.functions?.find(
      (func) => Object.keys(func?.paginate ?? {}).length > 0,
    );

    const deltaPaginateFunc = properties.functions?.find(
      (func) => Object.keys(func?.deltaPaginate ?? {}).length > 0,
    );

    if (selectionFunc) {
      const selectedValues = selectionFunc?.selection?.values;

      if (properties.groupByField?.field === EqlFieldName.Source) {
        // To respect the concept of namespace, we save the visibleDataPoints in
        // form of {name,namespace}[] i.e the source filter representation
        // for every saved sources. This now coincides with how we represent sources
        // at different places
        const reversedSourceDetailsMap = getReversedSourceDetailMap(
          sourceDetailsMap ?? new Map(),
        );
        const sourceDetails = toSourceFilter(
          { sources: selectedValues ?? [] },
          reversedSourceDetailsMap,
        );

        return {
          shouldShowTopDataPoints: false,
          visibleDataPoints: sourceDetails.sources.map((sourceDetails) => {
            return JSON.stringify(sourceDetails);
          }),
        };
      }

      return {
        shouldShowTopDataPoints: false,
        visibleDataPoints: selectedValues,
      };
    }

    if (paginateFunc) {
      return {
        shouldShowTopDataPoints: true,
        topDataPointsSelectionCount: getFixedTopX(
          paginateFunc?.paginate?.pageSize,
        ),
      };
    }

    if (deltaPaginateFunc) {
      return {
        shouldShowTopDataPoints: true,
        topDataPointsSelectionCount: getFixedTopX(
          deltaPaginateFunc?.deltaPaginate?.pageSize,
        ),
      };
    }
  };

  const getEQLFunctionName = () => {
    if (
      properties.formulaQuery?.eqlFunction &&
      EQLFunctionNameMap[properties.formulaQuery?.eqlFunction] ===
        EQLFunctionName.Count &&
      getVisualisationType() === QuantifyVisualisationType.Pie
    ) {
      return EQLFunctionName.NormaliseByPercent;
    }

    return properties.formulaQuery?.eqlFunction
      ? EQLFunctionNameMap[properties.formulaQuery?.eqlFunction]
      : undefined;
  };

  const getDenominatorWhereClause = () => {
    const denominatorWhereClause =
      sourceDetailsMap && properties.formulaQuery?.denominatorWhereClause
        ? parseExpression(
            properties.formulaQuery?.denominatorWhereClause,
            sourceDetailsMap,
          )
        : undefined;

    if (denominatorWhereClause) {
      return denominatorWhereClause;
    }

    /**
     * ##TODO: Remove this after 01 May 2025
     * Workaround to set missing denominator for old pie charts with count formula
     * Which is absent in the eql properties due to incorrect eql string
     *
     */
    if (
      parsedMetadata?.metadata?.activeVisualisationType ===
        QuantifyVisualisationType.Pie &&
      parsedMetadata?.query?.formulaID === EQLFunctionName.Count &&
      createdAt < 1725555600
    ) {
      return whereClause;
    }
  };

  return {
    visual: {
      ...quantifyInitialState.visual,
      shouldShowCategories: properties.groupByField?.field === 'entityCategory',
      isCustomAnomalyThresholdSelected:
        DEFAULT_ANOMALY_THRESHOLD !== getAnomalyInfo()?.threshold,
    },
    query: {
      ...getTopXProperties(),
      type: EqlQueryType.Aggregates,
      whereClause: whereClause,
      compareMetadata: getCompareMetadata(),
      visualisationType: getVisualisationType(),
      isCoOccurring: properties.isCoOccurring,
      sortBy: getSortBy(),
      aggregatePeriod: properties.aggregatePeriod ?? TimeUnit.Month,
      compareQuery: properties.compareQuery
        ? {
            ...properties.compareQuery,
            timerange: properties.compareQuery.timerange
              ? {
                  ...properties.compareQuery.timerange,
                  startAfter: properties.compareQuery.timerange?.startAfter
                    ? properties.compareQuery.timerange?.startAfter * 1000
                    : undefined,
                  endBefore: properties.compareQuery.timerange?.endBefore
                    ? properties.compareQuery.timerange?.endBefore * 1000
                    : undefined,
                }
              : undefined,
            whereClause:
              properties.compareQuery?.whereClause && sourceDetailsMap
                ? parseExpression(
                    properties.compareQuery?.whereClause,
                    sourceDetailsMap,
                  )
                : undefined,
            formula: {
              ...properties.compareQuery?.formula,
              denominatorWhereClause:
                properties.compareQuery?.formula?.denominatorWhereClause &&
                sourceDetailsMap
                  ? parseExpression(
                      properties.compareQuery?.formula.denominatorWhereClause,
                      sourceDetailsMap,
                    )
                  : undefined,
            },
          }
        : undefined,
      groupByField: properties.groupByField ?? FEEDBACK_RECORDS_EQL_FIELD,
      duration: getDurationFromAssistantResponseEqlQuery(properties.duration),
      formulaQuery: {
        ...properties.formulaQuery,
        eqlFunction: getEQLFunctionName(),
        denominatorWhereClause: getDenominatorWhereClause(),
      },
      shouldShowAnomalies: !!getAnomalyInfo(),
      anomalyThreshold:
        getAnomalyInfo()?.threshold ?? DEFAULT_ANOMALY_THRESHOLD,
    },
  };
};
